import React, { useState } from "react";
import Box from "@mui/material/Box";
import UserLayout from "../../../user";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import moment from "moment";
import { Avatar, Button, Table, TableBody, TableCell, TableRow } from "@mui/material";
import TableHead from "@mui/material/TableHead";
import TableContainer from "@mui/material/TableContainer";
import Paper from "@mui/material/Paper";
import { useNavigate } from "react-router";
import PDF from "../../../../assets/images/pdf.png";
import word from "../../../../assets/images/word.png";
import CSV from "../../../../assets/images/csv.png";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import AttachFileOutlinedIcon from "@mui/icons-material/AttachFileOutlined";
import StickyNote2OutlinedIcon from "@mui/icons-material/StickyNote2Outlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import AppointmentReschedule from "../../../../components/card-appointment/AppointmentReschedule";
import AppointmentSchedule from "../../../../components/card-appointment/AppointmentSchedule";
import { AppointmentAllStatusArray } from "../../../../services/utills";
import { content, heading, wallet } from "../../../../components/card-appointment/CardAppointment";
import { useDispatch, useSelector } from "react-redux";
import UserInvoices from "../../../user/user-bookings/user-invoices/userInvoices";
import { storeUserInvoice } from "../../../../store/booking-widget";
import NOShowmodal from "../../../../components/modal/NoShowmodal";

const ACCENT = "#B71F58";

const cardStyle = {
  background: "#fff",
  borderRadius: "14px",
  border: "1px solid #F0EFEF",
  padding: { sm: "24px 28px", xs: "16px" },
  mb: "16px",
};

const sectionHeading = {
  color: ACCENT,
  fontSize: "11px",
  letterSpacing: "1.5px",
  fontFamily: "helvetica-lt-std-bold",
  textTransform: "uppercase",
  mb: "8px",
};

const headingIcon = { fontSize: "13px", mr: "4px", verticalAlign: "middle" };

const sectionLabelMuted = {
  color: ACCENT,
  fontSize: "11px",
  letterSpacing: "1.5px",
  fontFamily: "helvetica-lt-std-bold",
  textTransform: "uppercase",
};

const sectionLabelAccent = {
  color: ACCENT,
  fontSize: "11px",
  letterSpacing: "1.5px",
  fontFamily: "helvetica-lt-std-bold",
  textTransform: "uppercase",
};

const getInitials = (name) => {
  if (!name) return "";
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
};

const NOTE_PREVIEW_LIMIT = 200;

const ExpandableNote = ({ text }) => {
  const [expanded, setExpanded] = useState(false);
  const content = text || "";
  const isLong = content.length > NOTE_PREVIEW_LIMIT;

  return (
    <Typography
      sx={{
        color: "#000",
        fontSize: "13px",
        fontFamily: "helvetica-lt-std-roman",
        wordBreak: "break-word",
        whiteSpace: "pre-wrap",
      }}
    >
      {isLong && !expanded ? `${content.slice(0, NOTE_PREVIEW_LIMIT).trimEnd()}... ` : content}
      {isLong && (
        <Box
          component="span"
          onClick={() => setExpanded((prev) => !prev)}
          sx={{
            color: ACCENT,
            cursor: "pointer",
            fontFamily: "helvetica-lt-std-bold",
            ml: expanded ? "6px" : 0,
          }}
        >
          {expanded ? "See less" : "See more"}
        </Box>
      )}
    </Typography>
  );
};

const AppointmentDetails = ({ appointmentDetails }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const authStore = useSelector((state) => state?.authStore);
  const { user } = authStore;
  const bookingWidgetStore = useSelector((state) => state?.bookingWidgetStore);
  const {
    userInvoice,
    userBookings,
    userBookingStatus,
    filterBy,
    pageLimit,
    currentPage,
    search,
  } = bookingWidgetStore;

  const handleAppointmentClick = (url) => {
    navigate(url, {
      state: { from: location.pathname },
    });
  };

  const [isExpanded, setIsExpanded] = useState(false);
  // Services plus any retail products sold on the booking.
  const detailItems = [...(appointmentDetails?.services || []), ...(appointmentDetails?.product_lines || [])];
  const displayedServices = isExpanded
    ? detailItems
    : detailItems.slice(0, 1);

  const handleToggle = () => {
    setIsExpanded((prevState) => !prevState);
  };

  const computeDuration = () => {
    if (appointmentDetails?.duration) return `${appointmentDetails.duration} min`;
    if (!appointmentDetails?.start_time || !appointmentDetails?.end_time) return "";
    const start = moment(appointmentDetails.start_time, ["h:mm A", "HH:mm"]);
    const end = moment(appointmentDetails.end_time, ["h:mm A", "HH:mm"]);
    const m = end.diff(start, "minutes");
    return m > 0 ? `${m} min` : "";
  };

  const formatNoteTime = (date) =>
    `${moment(date).format("D MMM, YYYY")} · ${moment(date).format("hh:mm A")}`;

  const invoiceId = appointmentDetails?.invoices?.invoices_methods?.[0]?.id;

  return (
    <Box
      sx={{
        background: "#F5F4F4",
        padding: { sm: "8px", xs: "8px" },
        minHeight: "calc(100dvh - 295px) !important",
      }}
    >
      {appointmentDetails?.plan?.id && (
        <Grid
          item
          xs={12}
          sx={{
            ...cardStyle,
            p: { sm: "28px", xs: "16px" },
            mb: "16px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <Box>
              <Typography
                variant="body2"
                color="initial"
                sx={{
                  color: "#000",
                  textDecoration: "underline",
                  fontSize: "16px",
                  fontFamily: "helvetica-lt-std-bold",
                }}
              >
                Bundle Details:
              </Typography>
              {appointmentDetails?.is_plan === 0 ? (
                <Box sx={{ display: "flex", mt: 2 }}>
                  <Typography
                    variant="body2"
                    color="initial"
                    sx={{
                      minWidth: "100px",
                      fontSize: "16px",
                      fontFamily: "helvetica-lt-std-bold",
                      color: "#000",
                    }}
                  >
                    Bundle Name:
                  </Typography>
                  <Typography
                    variant="body2"
                    color="initial"
                    sx={{
                      ml: 1,
                      fontSize: "16px",
                      fontFamily: "helvetica-lt-std-bold",
                      color: "#000",
                    }}
                  >
                    {appointmentDetails?.plan?.title}
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ display: "flex", mt: 2 }}>
                  <Typography
                    variant="body2"
                    color="initial"
                    sx={{
                      minWidth: "100px",
                      fontSize: "16px",
                      fontFamily: "helvetica-lt-std-bold",
                      color: "#000",
                    }}
                  >
                    Session No:
                  </Typography>
                  <Typography
                    variant="body2"
                    color="initial"
                    sx={{
                      ml: 1,
                      fontSize: "16px",
                      fontFamily: "helvetica-lt-std-bold",
                      color: "#000",
                    }}
                  >
                    {appointmentDetails?.current_session}
                  </Typography>
                </Box>
              )}

              <Box sx={{ display: "flex", mt: 1 }}>
                <Typography
                  variant="body2"
                  color="initial"
                  sx={{
                    minWidth: "100px",
                    fontSize: "16px",
                    fontFamily: "helvetica-lt-std-bold",
                    color: "#000",
                  }}
                >
                  Completed Session:
                </Typography>
                <Typography
                  variant="body2"
                  color="initial"
                  sx={{
                    ml: 1,
                    fontSize: "16px",
                    fontFamily: "helvetica-lt-std-bold",
                    color: "#000",
                  }}
                >
                  {appointmentDetails?.completed_session || 0} /{" "}
                  {appointmentDetails?.plan?.no_of_bookings}
                </Typography>
              </Box>
            </Box>
          </Box>
          <Grid
            container
            sx={{
              display: "flex",
              justifyContent: "space-between",
              mt: "24px",
            }}
          >
            <Grid item lg={3.5} sm={3.5} xs={12}>
              <Box>
                <Typography
                  variant="body2"
                  color="initial"
                  sx={{
                    color: "#000",
                    textDecoration: "underline",
                    fontSize: "16px",
                    fontFamily: "helvetica-lt-std-bold",
                  }}
                >
                  Bundle Total Amount:
                </Typography>
                <Box sx={{ display: "flex", mt: 2 }}>
                  <Typography
                    variant="body2"
                    color="initial"
                    sx={{ ml: 1, color: "#000" }}
                  >
                    ${appointmentDetails?.plan_total_amount}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item lg={3.5} sm={3.5} xs={12}>
              <Box>
                <Typography
                  variant="body2"
                  color="initial"
                  sx={{
                    color: "#000",
                    textDecoration: "underline",
                    fontSize: "16px",
                    fontFamily: "helvetica-lt-std-bold",
                  }}
                >
                  Received Amount:
                </Typography>
                <Box sx={{ display: "flex", mt: 2 }}>
                  <Typography
                    variant="body2"
                    color="initial"
                    sx={{ ml: 1, color: "#000" }}
                  >
                    ${appointmentDetails?.plan_received_amount}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item lg={3.5} sm={3.5} xs={12}>
              <Box>
                <Typography
                  variant="body2"
                  color="initial"
                  sx={{
                    color: "#000",
                    textDecoration: "underline",
                    fontSize: "16px",
                    fontFamily: "helvetica-lt-std-bold",
                  }}
                >
                  Remaining Amount:
                </Typography>
                <Box sx={{ display: "flex", mt: 2 }}>
                  <Typography
                    variant="body2"
                    color="initial"
                    sx={{ ml: 1, color: "#000" }}
                  >
                    ${appointmentDetails?.plan_remaining_amount}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
          <TableContainer
            component={Paper}
            sx={{
              mt: "24px",
              boxShadow: "none",
              border: "1px solid #F0EFEF",
            }}
          >
            <Table sx={{ minWidth: 650 }} size="small" aria-label="a dense table">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: "#000", fontFamily: "helvetica-lt-std-bold" }}>
                    Session No
                  </TableCell>
                  <TableCell sx={{ color: "#000", fontFamily: "helvetica-lt-std-bold" }}>
                    Status
                  </TableCell>
                  <TableCell sx={{ color: "#000", fontFamily: "helvetica-lt-std-bold" }}>
                    Date & Time
                  </TableCell>
                  <TableCell sx={{ color: "#000", fontFamily: "helvetica-lt-std-bold" }}>
                    Booking Ref
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {appointmentDetails?.related_appointments?.length > 0 &&
                  appointmentDetails?.related_appointments.map((row, rowIndex) => (
                    <TableRow
                      key={rowIndex}
                      sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                    >
                      <TableCell scope="row">
                        <Box
                          sx={{
                            width: "max-content",
                            borderRadius: "8px",
                            p: "5px 10px",
                            border: `1px solid ${row?.status_color || "black"}`,
                            cursor: "pointer",
                          }}
                          onClick={() => {
                            navigate(`/crm/appointment/details/${row?.id}`);
                          }}
                        >
                          <Typography
                            sx={{
                              color: row?.status_color || "black",
                            }}
                          >
                            Session No {row.current_session}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography
                          sx={{
                            background: row?.status_color,
                            p: "2px 22px",
                            borderRadius: "13px",
                            color: "#fff",
                            ml: 1,
                            width: "130px",
                          }}
                        >
                          {row.status}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          sx={{
                            color: row.date ? "#000" : "#F25022",
                          }}
                        >
                          {row.date ? (
                            moment(row.date).format("MMMM DD, YYYY")
                          ) : (
                            <Grid item lg={10} xl={8} sm={10} md={10} xs={12}>
                              <AppointmentSchedule
                                fwidth={"100%"}
                                appointment={row}
                                buttonText="Book Now"
                              />
                            </Grid>
                          )}
                        </Typography>
                        <Typography sx={{ color: "#000" }}>
                          {row.date ? row.start_time + " - " + row.end_time : ""}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{
                            width: "max-content",
                            borderRadius: "8px",
                            p: "5px 10px",
                            border: `1px solid ${row?.status_color || "black"}`,
                            cursor: "pointer",
                          }}
                          onClick={() => {
                            navigate(`/crm/appointment/details/${row?.id}`);
                          }}
                        >
                          <Typography
                            sx={{
                              color: row?.status_color || "black",
                            }}
                          >
                            Booking Ref {row?.id}
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      )}

      <UserLayout>
        {/* Top card: Date / Booking Status / Payment Status + Service row */}
        <Box sx={cardStyle}>
          <Grid container>
            <Grid
              item
              xs={12}
              sm={4}
              sx={{
                borderRight: { sm: "1px solid #F0EFEF" },
                pr: { sm: "20px" },
                pb: { xs: "16px", sm: 0 },
              }}
            >
              <Typography sx={sectionHeading}>
                <EventOutlinedIcon sx={headingIcon} />
                Appointment Date
              </Typography>
              <Typography
                sx={{
                  color: appointmentDetails?.date ? "#000" : "#F25022",
                  fontSize: { sm: "28px", xs: "22px" },
                  fontFamily: "helvetica-lt-std-bold",
                  lineHeight: 1.1,
                }}
              >
                {appointmentDetails?.date
                  ? moment(appointmentDetails?.date).format("ddd, MMM D")
                  : "Date Not Set"}
              </Typography>
              {appointmentDetails?.date && (
                <Typography
                  sx={{
                    color: "#000",
                    fontSize: "13px",
                    mt: "8px",
                    fontFamily: "helvetica-lt-std-roman",
                  }}
                >
                  {appointmentDetails?.start_time} – {appointmentDetails?.end_time}
                  {computeDuration() ? ` · ${computeDuration()}` : ""}
                </Typography>
              )}
            </Grid>

            <Grid
              item
              xs={12}
              sm={4}
              sx={{
                borderRight: { sm: "1px solid #F0EFEF" },
                pl: { sm: "24px" },
                pr: { sm: "20px" },
                py: { xs: "16px", sm: 0 },
              }}
            >
              <Typography sx={sectionHeading}>
                <DescriptionOutlinedIcon sx={headingIcon} />
                Booking Status
              </Typography>
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "8px",
                  background: "#FCE8EC",
                  color: "#000",
                  padding: "6px 14px",
                  borderRadius: "20px",
                  fontSize: "12px",
                  fontFamily: "helvetica-lt-std-bold",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  mb: "12px",
                }}
              >
                <Box
                  sx={{
                    width: "7px",
                    height: "7px",
                    borderRadius: "50%",
                    background: appointmentDetails?.status_color || "#B71F58",
                  }}
                />
                {appointmentDetails?.status === "NoShow"
                  ? "No Show"
                  : appointmentDetails?.status}
              </Box>
              <Typography
                sx={{
                  color: "#000",
                  fontSize: "13px",
                  fontFamily: "helvetica-lt-std-roman",
                }}
              >
                Booking{" "}
                <span
                  style={{
                    color: user?.role_id === 3 ? "#000" : ACCENT,
                    cursor: user?.role_id === 3 ? "default" : "pointer",
                  }}
                  onClick={
                    user?.role_id === 3
                      ? undefined
                      : () =>
                          handleAppointmentClick(
                            `/crm/appointment/details/${appointmentDetails?.id}`
                          )
                  }
                >
                  #{appointmentDetails?.id}
                </span>
                {invoiceId && (
                  <>
                    {" · "}Invoice{" "}
                    <span
                      style={{ color: ACCENT, cursor: "pointer" }}
                      onClick={async () => {
                        if (user?.role_id === 3) {
                          await dispatch(storeUserInvoice(appointmentDetails));
                          navigate("/user/appointment/invoice");
                        } else {
                          handleAppointmentClick(
                            `/crm/invoices/${appointmentDetails?.id}`
                          );
                        }
                      }}
                    >
                      #{invoiceId}
                    </span>
                  </>
                )}
              </Typography>
            </Grid>

            <Grid
              item
              xs={12}
              sm={4}
              sx={{
                pl: { sm: "24px" },
                pt: { xs: "16px", sm: 0 },
              }}
            >
              <Typography sx={sectionHeading}>
                <AttachFileOutlinedIcon sx={headingIcon} />
                Payment Status
              </Typography>
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "8px",
                  background:
                    appointmentDetails?.payment_status === "Paid" ||
                    appointmentDetails?.payment_status === "Pre Paid"
                      ? "#E8F5EE"
                      : "#FCE8EC",
                  color: "#000",
                  padding: "6px 14px",
                  borderRadius: "20px",
                  fontSize: "12px",
                  fontFamily: "helvetica-lt-std-bold",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  mb: "12px",
                }}
              >
                <Box
                  sx={{
                    width: "7px",
                    height: "7px",
                    borderRadius: "50%",
                    background:
                      appointmentDetails?.payment_status === "Paid" ||
                      appointmentDetails?.payment_status === "Pre Paid"
                        ? "#1C7E46"
                        : "#C64949",
                  }}
                />
                {appointmentDetails?.payment_status}
              </Box>
              <Typography
                sx={{
                  color: "#000",
                  fontSize: "13px",
                  fontFamily: "helvetica-lt-std-roman",
                }}
              >
                Grand total{" "}
                <span style={{ fontFamily: "helvetica-lt-std-bold", color: "#000" }}>
                  ${appointmentDetails?.grand_total}
                </span>
              </Typography>
            </Grid>
          </Grid>

          {/* Service row */}
          {detailItems.length > 0 && (
            <Box sx={{ borderTop: "1px solid #F0EFEF", mt: "20px", pt: "18px" }}>
              {displayedServices?.map((service, idx) => (
                <Box
                  key={idx}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "16px",
                    mb: idx < displayedServices.length - 1 ? "12px" : 0,
                    flexWrap: { xs: "wrap", sm: "nowrap" },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: "14px", flexGrow: 1 }}>
                    <Box
                      sx={{
                        width: "38px",
                        height: "38px",
                        borderRadius: "50%",
                        background: "#FCE8EC",
                        color: ACCENT,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "14px",
                        fontFamily: "helvetica-lt-std-bold",
                        flexShrink: 0,
                      }}
                    >
                      {service?.name?.[0]?.toUpperCase() || "S"}
                    </Box>
                    <Box>
                      <Typography
                        sx={{
                          color: "#000",
                          fontSize: "15px",
                          fontFamily: "helvetica-lt-std-bold",
                        }}
                      >
                        {service?.name}
                        {appointmentDetails?.type === "Consultation"
                          ? " — In Person Consultation"
                          : appointmentDetails?.type === "Virtual Consultation"
                          ? " — Virtual"
                          : service?.category
                          ? ` — ${service.category}`
                          : ""}
                      </Typography>
                      <Typography
                        sx={{
                          color: "#000",
                          fontSize: "12px",
                          mt: "2px",
                          fontFamily: "helvetica-lt-std-roman",
                        }}
                      >
                        {service?.is_product_line
                          ? "product"
                          : `${appointmentDetails?.services?.length} service${appointmentDetails?.services?.length > 1 ? "s" : ""}`}
                        {computeDuration() ? ` · ${computeDuration()}` : ""}
                        {appointmentDetails?.provider?.name
                          ? ` · ${appointmentDetails?.provider?.name}`
                          : ""}
                        {appointmentDetails?.reference_clinic_name
                          ? ` · ${appointmentDetails?.reference_clinic_name}`
                          : ""}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ textAlign: "right" }}>
                    <Typography
                      sx={{
                        color: "#000",
                        fontSize: "18px",
                        fontFamily: "helvetica-lt-std-bold",
                      }}
                    >
                      ${service?.booking_cost}
                    </Typography>
                    <Typography
                      sx={{
                        color: "#000",
                        fontSize: "11px",
                        fontFamily: "helvetica-lt-std-roman",
                      }}
                    >
                      {service?.is_product_line ? `qty ${service?.extra_quantity}` : "per session"}
                      {!service?.is_product_line &&
                      service?.extra_quantity !== null &&
                      service?.extra_quantity !== undefined
                        ? ` · extra (${service?.extra_quantity})`
                        : ""}
                    </Typography>
                  </Box>
                </Box>
              ))}
              {detailItems.length > 1 && (
                <Box sx={{ display: "flex", justifyContent: "center", mt: "8px" }}>
                  <Button
                    onClick={handleToggle}
                    variant="text"
                    sx={{
                      color: ACCENT,
                      fontFamily: "helvetica-lt-std-bold",
                      textTransform: "none",
                    }}
                  >
                    {isExpanded ? "Show Less" : "Show More..."}
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </Box>

        {/* Payment summary card */}
        <Box sx={cardStyle}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: "20px",
              flexWrap: "wrap",
              gap: "8px",
            }}
          >
            <Typography sx={sectionHeading}>
              <ReceiptLongOutlinedIcon sx={headingIcon} />
              Payment Summary
            </Typography>
            <Typography
              sx={{
                color: ACCENT,
                fontSize: "13px",
                cursor: "pointer",
                textDecoration: "underline",
                fontFamily: "helvetica-lt-std-bold",
              }}
              onClick={async () => {
                if (user?.role_id === 3) {
                  await dispatch(storeUserInvoice(appointmentDetails));
                  navigate("/user/appointment/invoice");
                } else {
                  handleAppointmentClick(`/crm/invoices/${appointmentDetails?.id}`);
                }
              }}
            >
              View invoice →
            </Typography>
          </Box>

          <Grid container spacing={{ xs: 2, md: 4 }}>
            <Grid item xs={12} md={7}>
              <Box>
                {appointmentDetails?.type === "Consultation" && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      py: "10px",
                      borderBottom: "1px dashed #F0EFEF",
                    }}
                  >
                    <Typography sx={{ color: "#000", fontSize: "14px" }}>
                      Deposit Amount
                    </Typography>
                    <Typography sx={{ color: "#000", fontSize: "14px" }}>
                      {appointmentDetails?.upfront_amount !== undefined
                        ? `$${appointmentDetails?.upfront_amount}`
                        : "$0"}
                    </Typography>
                  </Box>
                )}

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    py: "10px",
                    borderBottom: "1px dashed #F0EFEF",
                  }}
                >
                  <Typography sx={{ color: "#000", fontSize: "14px" }}>
                    Sub total
                  </Typography>
                  <Typography sx={{ color: "#000", fontSize: "14px" }}>
                    {appointmentDetails?.services_total_cost !== undefined
                      ? `$${appointmentDetails?.services_total_cost}`
                      : "$0"}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    py: "10px",
                    borderBottom: "1px dashed #F0EFEF",
                  }}
                >
                  <Typography sx={{ color: "#000", fontSize: "14px" }}>
                    Discount
                  </Typography>
                  <Typography sx={{ color: "#000", fontSize: "14px" }}>
                    {appointmentDetails?.invoices?.discount &&
                    parseFloat(appointmentDetails?.invoices?.discount) > 0
                      ? `- $${appointmentDetails?.invoices?.discount}`
                      : "$0"}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    py: "10px",
                    borderBottom: "1px dashed #F0EFEF",
                  }}
                >
                  <Typography sx={{ color: "#000", fontSize: "14px" }}>
                    Amount
                  </Typography>
                  <Typography sx={{ color: "#000", fontSize: "14px" }}>
                    {appointmentDetails?.invoices?.amount_after_add_discount !==
                    undefined
                      ? `$${appointmentDetails?.invoices?.amount_after_add_discount}`
                      : "$0"}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    py: "10px",
                    borderBottom: "1px dashed #F0EFEF",
                  }}
                >
                  <Typography sx={{ color: "#000", fontSize: "14px" }}>Tax</Typography>
                  <Typography sx={{ color: "#000", fontSize: "14px" }}>
                    {appointmentDetails?.invoices?.tax !== undefined
                      ? `$${appointmentDetails?.invoices?.tax}`
                      : "$0"}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    py: "10px",
                    borderBottom: "1px solid #F0EFEF",
                  }}
                >
                  <Typography sx={{ color: "#000", fontSize: "14px" }}>Tip</Typography>
                  <Typography sx={{ color: "#000", fontSize: "14px" }}>
                    {appointmentDetails?.invoices?.tip !== undefined
                      ? `$${appointmentDetails?.invoices?.tip}`
                      : "$0"}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    py: "14px",
                    borderBottom: "1px solid #F0EFEF",
                  }}
                >
                  <Typography sx={{ ...sectionLabelMuted, color: "#000", fontSize: "12px" }}>
                    GRAND TOTAL
                  </Typography>
                  <Typography
                    sx={{
                      color: ACCENT,
                      fontSize: "22px",
                      fontFamily: "helvetica-lt-std-bold",
                    }}
                  >
                    {appointmentDetails?.invoices?.amount_after_tip_and_tax !==
                    undefined
                      ? `$${Number(
                          appointmentDetails.invoices.amount_after_tip_and_tax
                        ).toFixed(2)}`
                      : "$0"}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    py: "14px",
                  }}
                >
                  <Typography sx={{ color: "#000", fontSize: "14px" }}>
                    Due amount
                  </Typography>
                  <Typography
                    sx={{
                      color: "#1C7E46",
                      fontSize: "14px",
                      fontFamily: "helvetica-lt-std-bold",
                    }}
                  >
                    {appointmentDetails?.due_amount !== undefined
                      ? `$${appointmentDetails?.due_amount}`
                      : "$0"}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={5}>
              <Box>
                <Typography sx={{ ...sectionLabelMuted, mb: "12px" }}>
                  PAYMENT METHOD
                </Typography>

                {appointmentDetails?.invoices?.invoices_methods?.length > 0 ? (
                  appointmentDetails?.invoices?.invoices_methods?.map((method, i) => (
                    <Box
                      key={i}
                      sx={{
                        background: "#FCE8EC",
                        borderRadius: "10px",
                        padding: "14px",
                        display: "flex",
                        alignItems: "center",
                        gap: "14px",
                        mb: "12px",
                        "@media (min-width: 900px) and (max-width: 1100px)": {
                          flexWrap: "wrap",
                          padding: "12px",
                          gap: "8px 10px",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          flexShrink: 0,
                          background: "#22308D",
                          color: "#fff",
                          borderRadius: "8px",
                          padding: "6px 10px",
                          fontSize: "11px",
                          fontFamily: "helvetica-lt-std-bold",
                          letterSpacing: "1px",
                          textTransform: "uppercase",
                          minWidth: "48px",
                          textAlign: "center",
                          flexShrink: 0,
                          "@media (min-width: 900px) and (max-width: 1000px)": {
                            gridRow: "1 / span 2",
                            alignSelf: "center",
                            padding: "6px 8px",
                            minWidth: "40px",
                            letterSpacing: "0.5px",
                          },
                        }}
                      >
                        {method?.payment_methods?.split(" ")[0] || "CASH"}
                      </Box>
                      <Box sx={{ flex: "1 1 0", minWidth: 0 }}>
                        <Typography
                          sx={{
                            color: "#000",
                            fontSize: "14px",
                            fontFamily: "helvetica-lt-std-bold",
                            overflowWrap: "anywhere",
                          }}
                        >
                          {method?.payment_methods}
                        </Typography>
                        <Typography
                          sx={{
                            color: "#000",
                            fontSize: "11px",
                            fontFamily: "helvetica-lt-std-roman",
                          }}
                        >
                          Captured at check-in
                        </Typography>
                      </Box>
                      <Typography
                        sx={{
                          color: "#000",
                          fontSize: "15px",
                          fontFamily: "helvetica-lt-std-bold",
                          whiteSpace: "nowrap",
                          flexShrink: 0,
                          "@media (min-width: 900px) and (max-width: 1100px)": {
                            flexBasis: "100%",
                            textAlign: "right",
                            pt: "8px",
                            borderTop: "1px dashed rgba(0, 0, 0, 0.15)",
                          },
                        }}
                      >
                        {method?.payment_methods !== "Future Payment" && "-"}$
                        {method?.amount}
                      </Typography>
                    </Box>
                  ))
                ) : (
                  <Typography sx={{ color: "#000", fontSize: "13px", mb: "20px" }}>
                    None
                  </Typography>
                )}

                <Typography sx={{ ...sectionLabelMuted, mt: "20px", mb: "12px" }}>
                  TRANSACTION
                </Typography>
                <Box sx={{ display: "flex", gap: "40px", flexWrap: "wrap" }}>
                  <Box>
                    <Typography sx={{ ...sectionLabelMuted, fontSize: "10px", mb: "4px" }}>
                      TRANSACTION ID
                    </Typography>
                    <Typography
                      sx={{
                        color: "#000",
                        fontSize: "14px",
                        fontFamily: "helvetica-lt-std-bold",
                      }}
                    >
                      {appointmentDetails?.invoices?.transaction_id || "— N/A"}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ ...sectionLabelMuted, fontSize: "10px", mb: "4px" }}>
                      CREATED
                    </Typography>
                    <Typography
                      sx={{
                        color: "#000",
                        fontSize: "14px",
                        fontFamily: "helvetica-lt-std-bold",
                      }}
                    >
                      {moment(appointmentDetails?.created_at).format("D MMM, YYYY")}
                    </Typography>
                    <Typography
                      sx={{
                        color: "#000",
                        fontSize: "11px",
                        fontFamily: "helvetica-lt-std-roman",
                      }}
                    >
                      {moment(appointmentDetails?.created_at).format("hh:mm A")}
                      {appointmentDetails?.reference_clinic_name
                        ? ` · ${appointmentDetails?.reference_clinic_name}`
                        : ""}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Notes & activity card */}
        {appointmentDetails?.role_id !== 3 && (
          <Box sx={cardStyle}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: "20px",
              }}
            >
              <Typography sx={sectionHeading}>
                <StickyNote2OutlinedIcon sx={headingIcon} />
                Notes & Activity
              </Typography>
              <Typography
                sx={{
                  color: ACCENT,
                  fontSize: "13px",
                  cursor: "pointer",
                  fontFamily: "helvetica-lt-std-bold",
                }}
              >
                + Add note
              </Typography>
            </Box>

            {/* Customer note */}
            <Box
              sx={{
                border: "1px solid #F0EFEF",
                borderRadius: "10px",
                p: "14px 16px",
                mb: "10px",
                display: "flex",
                gap: "12px",
              }}
            >
              <Box
                sx={{
                  width: "36px",
                  height: "36px",
                  borderRadius: "50%",
                  background: "#FCE8EC",
                  color: ACCENT,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "12px",
                  fontFamily: "helvetica-lt-std-bold",
                  flexShrink: 0,
                }}
              >
                CN
              </Box>
              <Box sx={{ flexGrow: 1 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    mb: "4px",
                    flexWrap: "wrap",
                  }}
                >
                  <Typography
                    sx={{
                      color: "#000",
                      fontSize: "13px",
                      fontFamily: "helvetica-lt-std-bold",
                    }}
                  >
                    Customer note
                  </Typography>
                  <Typography
                    sx={{
                      color: "#000",
                      fontSize: "12px",
                      fontStyle: "italic",
                      fontFamily: "helvetica-lt-std-roman",
                    }}
                  >
                    — from booking form
                  </Typography>
                  <Box
                    sx={{
                      background: "#FCE8EC",
                      color: ACCENT,
                      borderRadius: "6px",
                      padding: "2px 8px",
                      fontSize: "10px",
                      fontFamily: "helvetica-lt-std-bold",
                      letterSpacing: "0.5px",
                    }}
                  >
                    CLIENT
                  </Box>
                </Box>
                <ExpandableNote
                  text={
                    appointmentDetails?.appointment_customer_note &&
                    typeof appointmentDetails.appointment_customer_note === "object"
                      ? appointmentDetails.appointment_customer_note.description
                      : "None provided."
                  }
                />
              </Box>
            </Box>

            {/* Medical/Staff notes */}
            {appointmentDetails?.notes?.Medical?.length > 0 &&
              appointmentDetails?.notes?.Medical.map((medical, i) => (
                <Box
                  key={`med-${i}`}
                  sx={{
                    border: "1px solid #F0EFEF",
                    borderRadius: "10px",
                    p: "14px 16px",
                    mb: "10px",
                    display: "flex",
                    gap: "12px",
                  }}
                >
                  <Box
                    sx={{
                      width: "36px",
                      height: "36px",
                      borderRadius: "50%",
                      background: "#7B2D5C",
                      color: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "12px",
                      fontFamily: "helvetica-lt-std-bold",
                      flexShrink: 0,
                    }}
                  >
                    {getInitials(medical?.staff?.name)}
                  </Box>
                  <Box sx={{ flexGrow: 1 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        mb: "4px",
                        flexWrap: "wrap",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#000",
                          fontSize: "13px",
                          fontFamily: "helvetica-lt-std-bold",
                          textTransform: "capitalize",
                        }}
                      >
                        {medical?.staff?.name}
                      </Typography>
                      <Typography
                        sx={{
                          color: "#000",
                          fontSize: "12px",
                          fontStyle: "italic",
                          fontFamily: "helvetica-lt-std-roman",
                        }}
                      >
                        — {formatNoteTime(medical?.created_at)}
                      </Typography>
                      <Box
                        sx={{
                          background: "#7B2D5C",
                          color: "#fff",
                          borderRadius: "6px",
                          padding: "2px 8px",
                          fontSize: "10px",
                          fontFamily: "helvetica-lt-std-bold",
                          letterSpacing: "0.5px",
                        }}
                      >
                        STAFF
                      </Box>
                    </Box>
                    <ExpandableNote text={medical?.description} />
                  </Box>
                </Box>
              ))}

            {appointmentDetails?.notes?.Staff?.length > 0 &&
              appointmentDetails?.notes?.Staff.map((staff, i) => (
                <Box
                  key={`stf-${i}`}
                  sx={{
                    border: "1px solid #F0EFEF",
                    borderRadius: "10px",
                    p: "14px 16px",
                    mb: "10px",
                    display: "flex",
                    gap: "12px",
                  }}
                >
                  <Box
                    sx={{
                      width: "36px",
                      height: "36px",
                      borderRadius: "50%",
                      background: "#7B2D5C",
                      color: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "12px",
                      fontFamily: "helvetica-lt-std-bold",
                      flexShrink: 0,
                    }}
                  >
                    {getInitials(staff?.staff?.name)}
                  </Box>
                  <Box sx={{ flexGrow: 1 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        mb: "4px",
                        flexWrap: "wrap",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#000",
                          fontSize: "13px",
                          fontFamily: "helvetica-lt-std-bold",
                          textTransform: "capitalize",
                        }}
                      >
                        {staff?.staff?.name}
                      </Typography>
                      <Typography
                        sx={{
                          color: "#000",
                          fontSize: "12px",
                          fontStyle: "italic",
                          fontFamily: "helvetica-lt-std-roman",
                        }}
                      >
                        — {formatNoteTime(staff?.created_at)}
                      </Typography>
                      <Box
                        sx={{
                          background: "#7B2D5C",
                          color: "#fff",
                          borderRadius: "6px",
                          padding: "2px 8px",
                          fontSize: "10px",
                          fontFamily: "helvetica-lt-std-bold",
                          letterSpacing: "0.5px",
                        }}
                      >
                        STAFF
                      </Box>
                    </Box>
                    <ExpandableNote text={staff?.description} />
                  </Box>
                </Box>
              ))}

            {/* System log */}
            <Box
              sx={{
                border: "1px solid #F0EFEF",
                borderRadius: "10px",
                p: "14px 16px",
                display: "flex",
                gap: "12px",
              }}
            >
              <Box
                sx={{
                  width: "36px",
                  height: "36px",
                  borderRadius: "50%",
                  background: "#7B2D5C",
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "12px",
                  fontFamily: "helvetica-lt-std-bold",
                  flexShrink: 0,
                }}
              >
                SYS
              </Box>
              <Box sx={{ flexGrow: 1 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    mb: "4px",
                    flexWrap: "wrap",
                  }}
                >
                  <Typography
                    sx={{
                      color: "#000",
                      fontSize: "13px",
                      fontFamily: "helvetica-lt-std-bold",
                    }}
                  >
                    System log
                  </Typography>
                  <Typography
                    sx={{
                      color: "#000",
                      fontSize: "12px",
                      fontStyle: "italic",
                      fontFamily: "helvetica-lt-std-roman",
                    }}
                  >
                    — {formatNoteTime(appointmentDetails?.created_at)}
                  </Typography>
                  <Box
                    sx={{
                      background: "#E5EBFF",
                      color: "#22308D",
                      borderRadius: "6px",
                      padding: "2px 8px",
                      fontSize: "10px",
                      fontFamily: "helvetica-lt-std-bold",
                      letterSpacing: "0.5px",
                    }}
                  >
                    AUTO
                  </Box>
                </Box>
                <Typography
                  sx={{
                    color: "#000",
                    fontSize: "13px",
                    fontFamily: "helvetica-lt-std-roman",
                  }}
                >
                  Booking created and confirmed.
                </Typography>
              </Box>
            </Box>
          </Box>
        )}

        {/* Attachments (preserved for parity, shown only if present) */}
        {appointmentDetails?.attachments?.length > 0 && (
          <Box sx={cardStyle}>
            <Typography
              sx={{
                color: "#000",
                fontSize: "16px",
                fontFamily: "helvetica-lt-std-bold",
                mb: "16px",
              }}
            >
              Attachments
            </Typography>
            {appointmentDetails?.attachments.map((attachment, attactmentIndex) => (
              <Box
                sx={{
                  display: "flex",
                  gap: "10px",
                  flexWrap: { sm: "nowrap", xs: "wrap", lg: "wrap", md: "wrap" },
                  mb: "12px",
                }}
                key={attactmentIndex}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: { sm: "nowrap", xs: "wrap", lg: "wrap", md: "wrap" },
                  }}
                >
                  {attachment?.files?.length &&
                    attachment?.files?.map((file, fileIndex) => (
                      <Avatar
                        key={fileIndex}
                        src={
                          (file?.type === "pdf" && PDF) ||
                          (file?.type === "word" && word) ||
                          (file?.type === "csv" && CSV) ||
                          file?.file
                        }
                        onClick={() => window.open(file?.file, "_blank")}
                        sx={{
                          cursor: "pointer",
                          height: "180px",
                          width: "180px",
                          border: "1px solid #E8E8E8",
                        }}
                        variant="rounded"
                      />
                    ))}
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    flexWrap: { sm: "nowrap", xs: "wrap" },
                  }}
                >
                  <Box sx={{ display: "flex", ml: 2 }}>
                    <Typography
                      sx={{
                        color: "#000",
                        mt: 1,
                        mb: 1,
                        fontStyle: "italic",
                        fontFamily: "helvetica-lt-std-roman",
                      }}
                    >
                      {moment(attachment?.created_at)?.format("YYYY-MM-DD hh:mm A")}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", ml: 2 }}>
                    <Typography
                      sx={{
                        color: "#000",
                        fontFamily: "helvetica-lt-std-bold",
                        background: "#F2F2F2",
                        borderRadius: "3.49275px",
                        maxHeight: "25px",
                      }}
                    >
                      Comments:
                    </Typography>
                    <Box>
                      <Typography sx={{ color: "#000", ml: 1 }}>
                        {attachment?.comment}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        )}

        {/* Footer card: Assigned Staff / Booking Clinic / Created By */}
        <Box sx={{ ...cardStyle, p: { sm: "20px 28px", xs: "16px" } }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <Typography sx={{ ...sectionLabelMuted, mb: "6px" }}>
                ASSIGNED STAFF
              </Typography>
              <Typography
                sx={{
                  color: "#000",
                  fontSize: "14px",
                  fontFamily: appointmentDetails?.staff?.id
                    ? "helvetica-lt-std-bold"
                    : "helvetica-lt-std-roman",
                  fontStyle: appointmentDetails?.staff?.id ? "normal" : "italic",
                  textTransform: "capitalize",
                }}
              >
                {appointmentDetails?.staff?.id
                  ? appointmentDetails?.staff?.name
                  : "— Not assigned yet"}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography sx={{ ...sectionLabelMuted, mb: "6px" }}>
                DEFAULT · BOOKING CLINIC
              </Typography>
              <Typography
                sx={{
                  color: "#000",
                  fontSize: "14px",
                  fontFamily: "helvetica-lt-std-bold",
                }}
              >
                {appointmentDetails?.provider?.name &&
                  (() => {
                    const parts = appointmentDetails.provider.name.split(" ");
                    const first = parts[0];
                    const rest = parts.slice(1).join(" ");
                    return (
                      <>
                        <span style={{ color: ACCENT, fontStyle: "italic" }}>
                          {first}
                        </span>
                        {rest ? ` ${rest}` : ""}
                      </>
                    );
                  })()}
              </Typography>
              <Typography
                sx={{
                  color: "#000",
                  fontSize: "12px",
                  fontFamily: "helvetica-lt-std-roman",
                }}
              >
                Booked from {appointmentDetails?.reference_clinic_name}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography sx={{ ...sectionLabelMuted, mb: "6px" }}>
                CREATED BY
              </Typography>
              <Typography
                sx={{
                  color: "#000",
                  fontSize: "14px",
                  fontFamily: "helvetica-lt-std-bold",
                  textTransform: "capitalize",
                }}
              >
                {appointmentDetails?.created_by}
              </Typography>
              <Typography
                sx={{
                  color: "#000",
                  fontSize: "12px",
                  fontFamily: "helvetica-lt-std-roman",
                }}
              >
                {moment(appointmentDetails?.created_at).format("D MMM, YYYY")} ·{" "}
                {moment(appointmentDetails?.created_at).format("hh:mm A")}
              </Typography>
            </Grid>
          </Grid>
        </Box>

        {appointmentDetails?.status === "No Show" && (
          <Box sx={{ mt: "12px" }}>
            <NOShowmodal Data={appointmentDetails} />
          </Box>
        )}
      </UserLayout>
    </Box>
  );
};

export default AppointmentDetails;
